import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ArrowLeft, MapPin, Euro, Square, Home, BedDouble, Bath, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const PropertyDetails = ({ property, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-SN', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getStatusColor = (statut) => {
    switch (statut) {
      case "Disponible": return "bg-emerald-100 text-emerald-800";
      case "Vendu": return "bg-red-100 text-red-800";
      case "Loué": return "bg-blue-100 text-blue-800";
      case "Réservé": return "bg-orange-100 text-orange-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const nextImage = () => {
    if (property.images && property.images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % property.images.length);
    }
  };

  const prevImage = () => {
    if (property.images && property.images.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + property.images.length) % property.images.length);
    }
  };

  if (!property) {
    return <div className="p-6">Propriété non trouvée</div>;
  }

  const hasImages = property.images && property.images.length > 0;
  const currentImage = hasImages ? property.images[currentImageIndex] : 'https://images.unsplash.com/photo-1560518883-ce09059eeffa';

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onClose}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour aux Biens
        </Button>
        <Badge className={getStatusColor(property.statut)}>
          {property.statut}
        </Badge>
      </div>

      {/* Images Section */}
      <Card>
        <CardContent className="p-0">
          <div className="relative">
            <div 
              className="h-96 bg-cover bg-center"
              style={{
                backgroundImage: `url('${currentImage}')`
              }}
            >
              {hasImages && property.images.length > 1 && (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
                    onClick={prevImage}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
                    onClick={nextImage}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                    {currentImageIndex + 1} / {property.images.length}
                  </div>
                </>
              )}
            </div>
            {hasImages && property.images.length > 1 && (
              <div className="flex space-x-2 p-4 overflow-x-auto">
                {property.images.map((img, index) => (
                  <div
                    key={index}
                    className={`w-20 h-20 bg-cover bg-center rounded cursor-pointer border-2 flex-shrink-0 ${
                      index === currentImageIndex ? 'border-blue-500' : 'border-gray-200'
                    }`}
                    style={{ backgroundImage: `url('${img}')` }}
                    onClick={() => setCurrentImageIndex(index)}
                  />
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Property Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Title and Price */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-2xl mb-2">{property.titre}</CardTitle>
                  <div className="flex items-center text-slate-600 mb-4">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span>{property.adresse}, {property.ville}</span>
                    {property.quartier && <span>, {property.quartier}</span>}
                  </div>
                </div>
                <Badge variant="outline" className="text-lg px-3 py-1">
                  {property.type_propriete}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center text-emerald-600 font-bold text-2xl">
                  <Euro className="h-6 w-6 mr-2" />
                  {formatPrice(property.prix)}
                </div>
                <Badge variant="secondary" className="text-lg">
                  {property.type_transaction}
                </Badge>
              </div>
            </CardHeader>
          </Card>

          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed">{property.description}</p>
            </CardContent>
          </Card>
        </div>

        {/* Property Details Sidebar */}
        <div className="space-y-6">
          {/* Key Features */}
          <Card>
            <CardHeader>
              <CardTitle>Caractéristiques</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Square className="h-4 w-4 mr-2 text-slate-600" />
                  <span className="text-sm">Surface</span>
                </div>
                <span className="font-semibold">{property.surface} m²</span>
              </div>
              
              {property.nombre_pieces && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Home className="h-4 w-4 mr-2 text-slate-600" />
                    <span className="text-sm">Pièces</span>
                  </div>
                  <span className="font-semibold">{property.nombre_pieces}</span>
                </div>
              )}
              
              {property.nombre_chambres && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <BedDouble className="h-4 w-4 mr-2 text-slate-600" />
                    <span className="text-sm">Chambres</span>
                  </div>
                  <span className="font-semibold">{property.nombre_chambres}</span>
                </div>
              )}
              
              {property.nombre_salles_bain && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Bath className="h-4 w-4 mr-2 text-slate-600" />
                    <span className="text-sm">Salles de bain</span>
                  </div>
                  <span className="font-semibold">{property.nombre_salles_bain}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Additional Info */}
          <Card>
            <CardHeader>
              <CardTitle>Informations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center text-sm">
                <Calendar className="h-4 w-4 mr-2 text-slate-600" />
                <span>Ajouté le {format(new Date(property.date_ajout), "PPP", { locale: fr })}</span>
              </div>
              
              {property.latitude && property.longitude && (
                <div className="text-sm text-slate-600">
                  <MapPin className="h-4 w-4 mr-2 inline" />
                  GPS: {property.latitude.toFixed(6)}, {property.longitude.toFixed(6)}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contact Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button className="w-full bg-gradient-to-r from-blue-600 to-indigo-600">
                Programmer une visite
              </Button>
              <Button variant="outline" className="w-full">
                Contacter l'agent
              </Button>
              <Button variant="outline" className="w-full">
                Ajouter aux favoris
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetails;